import { useMutation, useQueryClient } from '@tanstack/react-query';
import { NavigateFunction } from 'react-router-dom';
import { notification } from 'antd';
import { api, setToken } from '../api';
import {
  clearUserFromLocalStorage,
  saveUserToLocalStorage,
} from '../utils/localstorage';
import { AuthUser } from '../interfaces';

interface UseLoginOptions {
  navigate: NavigateFunction;
  setUser: (user: AuthUser | null) => void;
}

interface LoginCredentials {
  email: string;
  password: string;
}

interface RegisterData {
  firstName: string;
  lastName: string;
  email: string;
  company?: string;
  password: string;
}

interface UseRegisterOptions {
  onSuccess?: () => void;
}

interface ApiError {
  response?: {
    status?: number;
    data?: {
      message?: string;
    };
  };
}

// Helper function to clear all user-related cache
const clearUserCache = (queryClient: ReturnType<typeof useQueryClient>) => {
  const cacheKeys = [
    ['attribution'],
    ['attributionQuota'],
    ['attribution-batch'],
    ['user'],
    ['bitcoinTransactions'],
    ['rootAddress'],
  ] as const;

  cacheKeys.forEach((queryKey) => {
    queryClient.removeQueries({ queryKey });
  });
};

export const useLogin = ({ navigate, setUser }: UseLoginOptions) => {
  const queryClient = useQueryClient();
  const [notifApi, contextHolder] = notification.useNotification();

  const mutation = useMutation({
    mutationFn: ({ email, password }: LoginCredentials) =>
      api.auth.login(email, password),
    onSuccess: (response) => {
      // Clear any existing cache first
      clearUserCache(queryClient);

      // Handle success
      saveUserToLocalStorage(response);
      setUser(response);
      setToken(response.token);

      // Show success notification
      notifApi.success({
        message: 'Login successful',
        description: 'You have successfully logged in',
        placement: 'topRight',
        duration: 1,
      });

      // Navigate to home page
      navigate('/');
    },
    onError: (error: ApiError) => {
      // Handle error
      const message = error?.response?.data?.message ?? 'An error occurred';
      notifApi.error({
        message: 'Login failed',
        description: message,
        placement: 'topRight',
        duration: 3,
      });
    },
    // Optional: Add retry logic for network errors only
    retry: (failureCount, error: ApiError) => {
      // Don't retry on authentication errors (401, 403)
      if (error?.response?.status === 401 || error?.response?.status === 403) {
        return false;
      }
      // Only retry network errors up to 2 times
      return failureCount < 2;
    },
  });

  return {
    ...mutation,
    contextHolder,
  };
};

export const useRegister = ({ onSuccess }: UseRegisterOptions = {}) => {
  const [notifApi, contextHolder] = notification.useNotification();

  const mutation = useMutation({
    mutationFn: (data: RegisterData) => api.auth.register(data),
    onSuccess: () => {
      notifApi.success({
        message: 'Registration successful',
        description: 'Your account has been created successfully',
        placement: 'topRight',
        duration: 3,
      });

      onSuccess?.();
    },
    onError: (error: ApiError) => {
      const message =
        error?.response?.data?.message ??
        'There was an error processing your request. Please try again';
      notifApi.error({
        message: 'Registration Error',
        description: message,
        placement: 'topRight',
        duration: 4.5,
      });
    },
  });

  return {
    ...mutation,
    contextHolder,
  };
};

interface UseLogoutOptions {
  navigate: NavigateFunction;
  setUser: (user: AuthUser | null) => void;
}

export const useLogout = ({ navigate, setUser }: UseLogoutOptions) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => api.auth.logout(),
    onSuccess: () => {
      clearUserCache(queryClient);
      clearUserFromLocalStorage();
      setUser(null);
      setToken(null);
      navigate('/');
    },
    onError: (error) => {
      console.error('Error during logout:', error);

      clearUserCache(queryClient);
      clearUserFromLocalStorage();
      setUser(null);
      setToken(null);
      navigate('/');
    },
  });
};
